import { useState } from 'react';
import PageSEO from '@/components/base/PageSEO';
import Breadcrumb from '@/components/base/Breadcrumb';
import { testimonials } from '@/mocks/data';

export default function AboutPage() {
  const [formStatus, setFormStatus] = useState<'idle' | 'success' | 'error'>('idle');
  const [formError, setFormError] = useState('');

  const aboutJsonLd = {
    '@context': 'https://schema.org',
    '@type': 'Organization',
    name: '福利铺子',
    url: 'https://fulipuzi.com',
    description: '福利铺子是您发现好物优惠、专属福利与超值折扣的首选平台。每天精选海量福利资源，涵盖购物返现、限时秒杀、会员特权等。',
    foundingDate: '2024',
    contactPoint: {
      '@type': 'ContactPoint',
      telephone: '+86-400-888-6666',
      contactType: 'customer service',
      email: 'contact@fulipuzi.com',
      availableLanguage: ['Chinese'],
    },
    address: {
      '@type': 'PostalAddress',
      streetAddress: '朝阳区建国路88号',
      addressLocality: '北京',
      addressCountry: 'CN',
    },
  };

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setFormStatus('idle');
    setFormError('');

    const form = e.currentTarget;
    const formData = new FormData(form);

    const hpField = formData.get('phone_alt');
    if (hpField && String(hpField).trim() !== '') {
      setFormStatus('success');
      form.reset();
      return;
    }

    try {
      const response = await fetch('https://readdy.ai/api/form/d9qoge7nahb7382ovcsg', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: new URLSearchParams(
          Array.from(formData.entries())
            .filter(([key]) => key !== 'phone_alt')
            .map(([key, value]) => [key, String(value)]),
        ).toString(),
      });

      const responseText = await response.text();
      let parsed;
      try {
        parsed = JSON.parse(responseText);
      } catch {
        parsed = null;
      }

      const serverMsg = parsed?.meta?.message || parsed?.message || responseText;

      if (response.ok && parsed?.code === 'OK') {
        setFormStatus('success');
        form.reset();
      } else {
        const isSpam = typeof serverMsg === 'string' && serverMsg.includes('spam');
        if (isSpam) {
          setFormStatus('success');
          form.reset();
        } else {
          setFormError(serverMsg || '提交失败，请稍后重试');
          setFormStatus('error');
        }
      }
    } catch {
      setFormError('网络错误，请检查网络连接后重试');
      setFormStatus('error');
    }
  };

  return (
    <>
      <PageSEO
        title="关于我们 - 福利铺子"
        description="福利铺子致力于为消费者提供最全面的福利优惠信息聚合服务。了解我们的品牌故事、用户口碑与联系方式。"
        keywords="福利铺子关于我们,福利铺子品牌介绍,福利铺子联系方式,福利铺子口碑"
        canonicalUrl="https://fulipuzi.com/about"
        schemaJsonLd={aboutJsonLd}
      />

      <div className="px-4 md:px-6 max-w-7xl mx-auto py-6 md:py-8">
        <Breadcrumb
          items={[
            { label: '首页', href: '/' },
            { label: '关于我们' },
          ]}
        />

        <section className="mb-12 md:mb-16">
          <div className="text-center mb-8">
            <h1 className="text-2xl md:text-4xl font-heading font-bold text-foreground-900 mb-3">
              关于福利铺子
            </h1>
            <p className="text-sm md:text-base text-foreground-600 max-w-2xl mx-auto">
              海量福利优惠一站汇聚，让省钱变得更简单
            </p>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 md:gap-10 items-center">
            <div>
              <h2 className="text-xl md:text-2xl font-heading font-bold text-foreground-900 mb-4">
                我们的故事
              </h2>
              <div className="space-y-3 text-sm md:text-base text-foreground-700 leading-relaxed">
                <p>
                  福利铺子成立于2024年，由一群热爱发现好物、乐于分享省钱技巧的年轻人创立。我们深知在信息爆炸的时代，消费者面对海量的促销信息往往无所适从——真假难辨的折扣、复杂烧脑的满减规则、稍纵即逝的秒杀时机，让本该愉快的购物体验变得疲惫不堪。
                </p>
                <p>
                  正因如此，福利铺子应运而生。我们致力于打造一个<strong>真实、高效、全面</strong>的福利优惠聚合平台，通过人工审核加智能筛选的方式，每天从全网海量促销信息中精选出最值得关注的福利资源。
                </p>
                <p>
                  从购物返现到限时秒杀，从会员特权到生活优惠，从数码3C到美妆护肤——福利铺子覆盖了消费者日常省钱的方方面面。我们不仅是优惠信息的搬运工，更是您精明消费的贴心伙伴。
                </p>
              </div>
            </div>
            <div className="w-full h-64 md:h-80 rounded-xl overflow-hidden">
              <img
                src="https://readdy.ai/api/search-image?query=Warm%20modern%20office%20team%20collaboration%20scene%20with%20young%20diverse%20people%20working%20together%20around%20laptop%2C%20soft%20natural%20lighting%2C%20minimal%20clean%20workspace%2C%20friendly%20creative%20atmosphere%2C%20lifestyle%20photography&width=800&height=600&seq=about-brand-01&orientation=landscape"
                alt="福利铺子团队"
                className="w-full h-full object-cover object-top rounded-xl"
              />
            </div>
          </div>
        </section>

        <section className="mb-12 md:mb-16">
          <div className="text-center mb-8">
            <h2 className="text-xl md:text-2xl font-heading font-bold text-foreground-900 mb-2">
              为什么选择我们
            </h2>
            <p className="text-sm text-foreground-600">四大核心优势，让省钱更省心</p>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 md:gap-5">
            {[
              {
                icon: 'ri-shield-check-line',
                title: '真实可靠',
                desc: '所有优惠信息均经人工审核验证，拒绝虚假折扣，确保每一分钱都省得实在。',
              },
              {
                icon: 'ri-flashlight-line',
                title: '极速更新',
                desc: '团队全天候监控优惠动态，限时秒杀、突发福利第一时间同步推送。',
              },
              {
                icon: 'ri-stack-line',
                title: '品类齐全',
                desc: '覆盖购物、美食、出行、娱乐等八大品类，一站式满足所有省钱需求。',
              },
              {
                icon: 'ri-heart-line',
                title: '用户至上',
                desc: '以用户省钱为第一目标，不接广告不收费，纯净体验只为你的福利。',
              },
            ].map((item, index) => (
              <div
                key={index}
                className="p-5 md:p-6 bg-white rounded-xl border border-background-200 text-center"
              >
                <div className="w-12 h-12 mx-auto mb-3 flex items-center justify-center bg-accent-100 text-accent-600 rounded-xl">
                  <i className={`${item.icon} text-xl`}></i>
                </div>
                <h3 className="font-heading font-semibold text-foreground-900 text-sm mb-2">
                  {item.title}
                </h3>
                <p className="text-xs md:text-sm text-foreground-500 leading-relaxed">
                  {item.desc}
                </p>
              </div>
            ))}
          </div>
        </section>

        <section className="mb-12 md:mb-16">
          <div className="text-center mb-8">
            <h2 className="text-xl md:text-2xl font-heading font-bold text-foreground-900 mb-2">
              用户口碑
            </h2>
            <p className="text-sm text-foreground-600">听听他们怎么说</p>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 md:gap-5">
            {testimonials.map((t) => (
              <div
                key={t.id}
                className="p-5 md:p-6 bg-white rounded-xl border border-background-200"
              >
                <div className="flex items-center gap-2 mb-3">
                  {[1, 2, 3, 4, 5].map((star) => (
                    <span key={star} className="w-4 h-4 flex items-center justify-center">
                      <i className="ri-star-fill text-accent-500 text-sm"></i>
                    </span>
                  ))}
                </div>
                <p className="text-sm text-foreground-700 leading-relaxed mb-4">
                  "{t.content}"
                </p>
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-full overflow-hidden bg-background-200">
                    <img
                      src={t.avatar}
                      alt={t.name}
                      className="w-full h-full object-cover"
                    />
                  </div>
                  <div>
                    <p className="text-sm font-medium text-foreground-900">{t.name}</p>
                    <p className="text-xs text-foreground-500">{t.role}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </section>

        <section id="contact" className="mb-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 md:gap-10">
            <div>
              <h2 className="text-xl md:text-2xl font-heading font-bold text-foreground-900 mb-4">
                联系我们
              </h2>
              <p className="text-sm text-foreground-600 mb-6">
                如有任何问题、建议或合作意向，欢迎通过以下方式联系我们。我们的团队会在1-2个工作日内回复。
              </p>
              <div className="space-y-4">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 flex items-center justify-center bg-primary-100 text-primary-600 rounded-xl flex-shrink-0">
                    <i className="ri-mail-line text-lg"></i>
                  </div>
                  <div>
                    <p className="text-xs text-foreground-500">电子邮箱</p>
                    <p className="text-sm font-medium text-foreground-800">contact@fulipuzi.com</p>
                  </div>
                </div>
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 flex items-center justify-center bg-primary-100 text-primary-600 rounded-xl flex-shrink-0">
                    <i className="ri-phone-line text-lg"></i>
                  </div>
                  <div>
                    <p className="text-xs text-foreground-500">客服电话</p>
                    <p className="text-sm font-medium text-foreground-800">400-888-6666</p>
                  </div>
                </div>
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 flex items-center justify-center bg-primary-100 text-primary-600 rounded-xl flex-shrink-0">
                    <i className="ri-map-pin-line text-lg"></i>
                  </div>
                  <div>
                    <p className="text-xs text-foreground-500">公司地址</p>
                    <p className="text-sm font-medium text-foreground-800">北京市朝阳区建国路88号</p>
                  </div>
                </div>
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 flex items-center justify-center bg-primary-100 text-primary-600 rounded-xl flex-shrink-0">
                    <i className="ri-time-line text-lg"></i>
                  </div>
                  <div>
                    <p className="text-xs text-foreground-500">工作时间</p>
                    <p className="text-sm font-medium text-foreground-800">周一至周五 9:00 - 18:00</p>
                  </div>
                </div>
              </div>
            </div>

            <div className="bg-white rounded-xl border border-background-200 p-5 md:p-6">
              <h3 className="font-heading font-semibold text-foreground-900 mb-4 text-base">
                发送消息
              </h3>
              <form onSubmit={handleSubmit} data-readdy-form="">
                <input
                  type="text"
                  name="phone_alt"
                  className="contact-form-hp"
                  tabIndex={-1}
                  autoComplete="off"
                  aria-hidden="true"
                  readOnly
                />
                <div className="flex flex-col gap-3">
                  <div>
                    <label htmlFor="about-name" className="block text-xs font-medium text-foreground-700 mb-1">
                      姓名 <span className="text-primary-500">*</span>
                    </label>
                    <input
                      id="about-name"
                      name="name"
                      type="text"
                      required
                      className="w-full px-3 py-2.5 text-sm border border-background-300 rounded-lg focus:outline-none focus:border-primary-400 focus:ring-1 focus:ring-primary-400 bg-background-50 text-foreground-800 transition-colors"
                      placeholder="请输入您的姓名"
                    />
                  </div>
                  <div>
                    <label htmlFor="about-email" className="block text-xs font-medium text-foreground-700 mb-1">
                      邮箱 <span className="text-primary-500">*</span>
                    </label>
                    <input
                      id="about-email"
                      name="email"
                      type="email"
                      required
                      className="w-full px-3 py-2.5 text-sm border border-background-300 rounded-lg focus:outline-none focus:border-primary-400 focus:ring-1 focus:ring-primary-400 bg-background-50 text-foreground-800 transition-colors"
                      placeholder="请输入您的邮箱"
                    />
                  </div>
                  <div>
                    <label htmlFor="about-subject" className="block text-xs font-medium text-foreground-700 mb-1">
                      主题
                    </label>
                    <input
                      id="about-subject"
                      name="subject"
                      type="text"
                      className="w-full px-3 py-2.5 text-sm border border-background-300 rounded-lg focus:outline-none focus:border-primary-400 focus:ring-1 focus:ring-primary-400 bg-background-50 text-foreground-800 transition-colors"
                      placeholder="请输入消息主题"
                    />
                  </div>
                  <div>
                    <label htmlFor="about-message" className="block text-xs font-medium text-foreground-700 mb-1">
                      留言内容 <span className="text-primary-500">*</span>
                    </label>
                    <textarea
                      id="about-message"
                      name="message"
                      required
                      maxLength={500}
                      rows={4}
                      className="w-full px-3 py-2.5 text-sm border border-background-300 rounded-lg focus:outline-none focus:border-primary-400 focus:ring-1 focus:ring-primary-400 bg-background-50 text-foreground-800 transition-colors resize-none"
                      placeholder="请输入您的留言内容（500字以内）"
                    ></textarea>
                  </div>
                  <button
                    type="submit"
                    className="w-full py-2.5 bg-primary-500 hover:bg-primary-600 text-background-50 rounded-lg text-sm font-medium transition-colors cursor-pointer whitespace-nowrap"
                  >
                    发送消息
                  </button>
                  {formStatus === 'success' && (
                    <p className="text-sm text-green-600 text-center">
                      消息已成功发送！我们会尽快与您联系。
                    </p>
                  )}
                  {formStatus === 'error' && (
                    <p className="text-sm text-red-600 text-center">
                      {formError || '发送失败，请稍后重试'}
                    </p>
                  )}
                </div>
              </form>
            </div>
          </div>
        </section>
      </div>
    </>
  );
}